import { Box, HStack } from "@chakra-ui/react";
import { FieldPathByValue, FieldValues, UseFormReturn } from "react-hook-form";
import Select from "components/Select";
import { Maybe, Schema as YupSchema } from "yup";
import CDS, { FloatingField } from "design-system";
import GreyBlackField from "components/DataDisplay/GreyBlackField";
import { TriggerSchema } from "features/Trigger/TemplateTrigger/Form/schema";
import generateSchedulingSummary from "features/Trigger/helpers";
import { reminderSchedulingFields } from "./fields";
import { TRIGGER_TYPE_REMINDER } from "./models";
import { ReminderTriggerSchema } from "./schema";

interface ReminderTriggerFormProps<TFieldName, TSchema extends FieldValues> {
  fieldName: TFieldName;
  form: UseFormReturn<TSchema>;
}

export default function ReminderTriggerForm<
  TSchema extends YupSchema,
  TFieldName extends FieldPathByValue<TSchema, Maybe<TriggerSchema>> = FieldPathByValue<TSchema, Maybe<TriggerSchema>>,
>({ fieldName, form }: ReminderTriggerFormProps<TFieldName, TSchema>) {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = form;
  const fields = reminderSchedulingFields(fieldName);
  const triggerWatch = watch(fieldName);
  const trigger = triggerWatch as ReminderTriggerSchema; // FIXME: Not able to get watch to appropriately type the resulting value, so casting for the moment. Should figure this out

  let schedulingSummary = "";
  if (trigger && trigger.type === TRIGGER_TYPE_REMINDER && trigger.data)
    schedulingSummary = generateSchedulingSummary({ field: fieldName, trigger });

  return (
    <>
      {schedulingSummary.length > 0 && (
        <CDS.Text12 fontSize="s" fontStyle="italic">{`This reminder will be sent: ${schedulingSummary}`}</CDS.Text12>
      )}
      <HStack width="100%" mb="1">
        <GreyBlackField label="When to send the reminder" minW="500px">
          <Box w="100%">
            <Select
              isClearable={false}
              required
              options={fields.combinedOffsetEvent.options}
              value={fields.combinedOffsetEvent.options.find(
                (option) =>
                  option.value.offsetDirection === trigger?.data?.offsetDirection &&
                  option.value.referenceEvent === trigger?.data?.referenceEvent,
              )}
              onChange={(selected: any) => {
                if (selected) {
                  setValue(`${fieldName}.data.offsetDirection`, selected.value.offsetDirection, { shouldDirty: true });
                  setValue(`${fieldName}.data.referenceEvent`, selected.value.referenceEvent, { shouldDirty: true });
                }
              }}
              errors={errors}
              control={control}
            />
          </Box>
        </GreyBlackField>
      </HStack>

      {trigger?.data?.offsetDirection && trigger.data.offsetDirection !== "exact" && (
        <HStack width="100%">
          <Box display="inline-block" width="25%">
            <FloatingField
              name={fields.offsetValue.name}
              errors={errors[fields.offsetValue.name]?.message as string | undefined}
              required
            >
              <FloatingField.Label>{fields.offsetValue.label}</FloatingField.Label>
              <FloatingField.StaticSelect
                control={control}
                name={fields.offsetValue.name}
                options={fields.offsetValue.options}
                isClearable={false}
                menuPlacement="top"
              />
            </FloatingField>
          </Box>
          <Box display="inline-block" width="40%">
            <FloatingField
              name={fields.offsetUnit.name}
              errors={errors[fields.offsetUnit.name]?.message as string | undefined}
              required
            >
              <FloatingField.Label>{fields.offsetUnit.label}</FloatingField.Label>
              <FloatingField.StaticSelect
                control={control}
                name={fields.offsetUnit.name}
                options={fields.offsetUnit.options}
                isClearable={false}
                menuPlacement="top"
              />
            </FloatingField>
          </Box>
        </HStack>
      )}
    </>
  );
}
